import React, { useState, useEffect } from "react";
import { jsPDF } from "jspdf";
import { trackPDFDownload, trackGoBack } from '../utils/analytics';

function Summary({ finalAnswers, onStartOver, onBack }) {
  const [questionsData, setQuestionsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch("/questions.json")
      .then(res => {
        if (!res.ok) {
          throw new Error(`Failed to fetch questions: ${res.status} ${res.statusText}`);
        }
        return res.json();
      })
      .then(json => {
        // Filter out disabled categories
        const enabledData = {
          ...json,
          categories: json.categories.filter(cat => cat.enabled !== false)
        };
        setQuestionsData(enabledData);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching questions.json:", err);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) return <div>Loading summary...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!questionsData) return <div>No questions data available</div>;

  const handleDownloadPDF = () => {
    const totalAnswers = Object.keys(finalAnswers).length;
    const totalQuestions = questionsData.categories.reduce((total, category) => total + category.questions.length, 0);
    
    trackPDFDownload(totalAnswers, totalQuestions);
    
    const doc = new jsPDF();
    
    // Set title and metadata
    const title = "Conversation Summary";
    const now = new Date();
    const dateFormatted = now.toLocaleDateString();
    const timeFormatted = now.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    const fullTimestamp = `${dateFormatted} at ${timeFormatted}`;
    
    // Add header
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 105, 15, { align: "center" });
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text("For discussion purposes only", 105, 25, { align: "center" });
    doc.text("Created on " + fullTimestamp, 105, 32, { align: "center" });
    
    // Add a subtle line
    doc.line(40, 40, 170, 40);
    
    // Add legal disclaimer section
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text("IMPORTANT LEGAL DISCLAIMER", 105, 50, { align: "center" });
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const disclaimers = [
      "• No Legal Effect: This document has NO LEGAL EFFECT and is not an advance directive or legally recognized document.",
      "• No Medical Advice: This tool does not provide medical advice. Consult qualified healthcare providers for medical decisions.",
      "• No Substitution: This does not substitute for proper advance care planning with legal and medical professionals.",
      "• No Liability: The creators accept no liability for decisions made with or without reference to this document.",
      "• In Emergencies: Medical professionals follow established protocols and state laws regardless of preferences here."
    ];
    
    let disclaimerY = 55;
    for (const line of disclaimers) {
      doc.text(line, 105, disclaimerY, { align: "center", maxWidth: 160 });
      disclaimerY += 6;
    }
    
    // Initialize vertical position
    let y = disclaimerY + 10; // Start content after the disclaimers
    
    // Loop through categories and questions
    questionsData.categories.forEach(category => {
      // Check page break
      if (y > 260) {
        doc.addPage();
        y = 20;
      }
      
      // Add category header
      doc.setFontSize(14);
      doc.text(category.name, 20, y);
      y += 10;
      
      // Loop through questions in the category
      category.questions.forEach(question => {
        // Check if user answered this question
        const userAnswer = finalAnswers[question.id];
        if (!userAnswer) return; // Skip if no answer
        
        const formattedAnswer = formatAnswer(question, userAnswer);
        const recommendation = generateRecommendation(question, userAnswer);
        
        // Check page break
        if (y > 260) {
          doc.addPage();
          y = 20;
        }
        
        // Add question text
        doc.setFontSize(11);
        doc.text(`Q: ${question.text}`, 20, y, { maxWidth: 170 });
        
        // Calculate next Y position based on text height
        // Rough estimate: each 80 chars = 1 line at maxWidth 170
        const lines = Math.ceil(question.text.length / 80) + 1;
        y += lines * 6;
        
        // Add answer and recommendation
        doc.setFontSize(10);
        doc.text(`A: ${formattedAnswer}`, 25, y, { maxWidth: 165 });

        // Calculate answer lines (account for newlines)
        const newlineCount = (formattedAnswer.match(/\n/g) || []).length;
        const answerLines = Math.ceil(formattedAnswer.length / 85) + 1 + newlineCount;
        y += answerLines * 6;
        
        doc.setFontSize(10);
        doc.text(`Discussion point: ${recommendation}`, 25, y, { maxWidth: 165 });
        
        // Calculate next Y position based on recommendation text height
        const recLines = Math.ceil(recommendation.length / 85) + 1;
        y += recLines * 6 + 4;
      });
      
      // Add space between categories
      y += 5;
    });
    
    // Add footer and legal disclaimers
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      
      // Add footer note on first page only
      if (i === 1) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'italic');
        doc.text("TIP: Use this document as a starting point for meaningful conversations", 105, 270, { align: "center" });
        doc.text("with family members and healthcare providers about your care preferences.", 105, 277, { align: "center" });
      }
      
      // Page numbering on all pages
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${i} of ${pageCount} | Created on ${fullTimestamp}`, 105, 290, { align: "center" });
    }
    
    // Save the PDF
    doc.save("conversation-summary.pdf");
  };

  const formatAnswer = (question, answer) => {
    if (!answer) return "No response";
    
    switch (question.type) {
      case 'contact_list':
        if (Array.isArray(answer) && answer.length > 0) {
          return answer.map((contact, index) => {
            const fields = question.fields.filter(f => f !== 'priority').map(field => {
              return `${field}: ${contact[field] || 'Not provided'}`;
            }).join(', ');
            return `${index + 1}. ${fields}`;
          }).join('\n');
        }
        return "No contacts provided";
      
      case 'text':
        return answer.trim() || "No response provided";
      
      case 'multiple_choice':
        if (answer.main) {
          let result = question.options.find(opt => opt.value === answer.main)?.text || answer.main;
          if (answer.conditional && answer.conditional.main) {
            result += ` (${answer.conditional.main})`;
          }
          return result;
        }
        return "No selection made";
      
      case 'yes_no':
        return answer;
      
      default:
        return String(answer);
    }
  };

  const generateRecommendation = (question, answer) => {
    if (!answer) return "No preference expressed";
    
    switch (question.type) {
      case 'contact_list':
        if (Array.isArray(answer) && answer.length > 0) {
          if (question.id === 'poa1') {
            return `Has designated ${answer.length} individual(s) to make healthcare decisions in order of priority`;
          } else if (question.id === 'poa2') {
            return `Has specified ${answer.length} individual(s) who should not make healthcare decisions`;
          }
        }
        return "No contacts specified";
      
      case 'text':
        return `Provided guidance: "${answer.substring(0, 100)}${answer.length > 100 ? '...' : ''}"`;
      
      case 'multiple_choice':
        if (answer.main) {
          const selectedOption = question.options.find(opt => opt.value === answer.main);
          if (selectedOption) {
            let rec = `Selected: ${selectedOption.text}`;
            if (answer.conditional && answer.conditional.main) {
              rec += ` with additional details: ${answer.conditional.main}`;
            }
            return rec;
          }
        }
        return "No clear preference expressed";
      
      case 'yes_no':
        return answer === 'Yes' ? 
          `Would accept this intervention` : 
          `Would decline this intervention`;
      
      default:
        return "Response recorded";
    }
  };

  // Organize recommendations by category for display
  const recommendationsByCategory = questionsData.categories.map(category => {
    const categoryQuestions = category.questions.map(question => {
      const answer = finalAnswers[question.id];
      const formattedAnswer = formatAnswer(question, answer);
      const recommendation = generateRecommendation(question, answer);
      
      return {
        id: question.id,
        text: question.text,
        answer: formattedAnswer,
        recommendation,
        hasAnswer: !!answer
      };
    }).filter(q => q.hasAnswer); // Only include answered questions
    
    return {
      name: category.name,
      questions: categoryQuestions
    };
  }).filter(cat => cat.questions.length > 0); // Only include categories with answers

  return (
    <div className="summary-container">
      
      <div className="summary-header">
        <div className="category-header-container">
          <button className="back-button" onClick={() => { trackGoBack('summary'); onBack(); }} aria-label="Go back">
            <span>←</span>
          </button>
          <h1 className="category-title">Conversation Summary</h1>
          <button className="reset-button" onClick={onStartOver} aria-label="Start over">
            <span>↺</span>
          </button>
        </div>
      </div>
      
      <div className="legal-disclaimer-box">
        <h3>Please Note</h3>
        <p>
          The preferences expressed in this summary:
        </p>
        <ul>
          <li>Are NOT legally binding medical directives</li>
          <li>Create NO obligations for healthcare providers</li>
          <li>Are meant ONLY for conversation and discussion purposes</li>
          <li>Do NOT replace formal advance care planning with qualified professionals</li>
        </ul>
      </div>
      
      <p className="summary-intro">
        Based on your responses, this conversation starter summary reflects
        potential preferences for discussion with family and healthcare providers.
      </p>
      
      <div className="download-container">
        <button 
          className="pdf-button" 
          onClick={handleDownloadPDF}
        >
          I Understand - Download Discussion Summary
        </button>
      </div>
      
      <div className="recommendations-container">
        {recommendationsByCategory.map((category, index) => (
          <div key={index} className="category-section">
            <h2>{category.name}</h2>
            {category.questions.map(question => (
              <div key={question.id} className="recommendation-item">
                <div className="scenario-label">Hypothetical scenario:</div>
                <div className="question-text">{question.text}</div>
                <div className="answer-row">
                  <span className="answer-label">Your response:</span>
                  <span className={`answer-value ${question.answer.toLowerCase()}`}>
                    {question.answer}
                  </span>
                </div>
                <div className="recommendation-text">
                  <strong>Discussion point:</strong> {question.recommendation}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      
      
      <div className="summary-footer">
        <p>
          This conversation tool is provided for informational purposes only and is not 
          intended to constitute legal or medical advice. The creators and operators of 
          this tool accept no liability for medical decisions made with or without 
          reference to any materials generated by this tool.
        </p>
      </div>
    </div>
  );
}

export default Summary;